import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Eyebrow } from "@/components/media/Sections";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const F = ({ label, children }) => (
  <div>
    <Label className="mono text-[11px] tracking-widest text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const Pick = ({ value, onChange, options, testId }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        data-testid={`${testId}-${o.replace(/\s+/g, "-").toLowerCase()}`}
        onClick={() => onChange(o)}
        className={`mono border px-4 py-2.5 text-[11px] tracking-widest transition-colors ${
          value === o
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border text-muted-foreground hover:border-primary/60 hover:text-primary"
        }`}
      >
        {o.toUpperCase()}
      </button>
    ))}
  </div>
);

const YES_NO = ["Yes", "No", "Not sure"];

export const ApplyForm = ({ apply }) => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", state: "",
    why_business: "", current_work: "", what_would_change: "",
    operate_yourself: "", comfortable_with_events: "",
    path: params.get("path") || "", start_timeline: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pick = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/applications", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div data-testid="apply-success" className="border border-primary bg-[#191603] p-8 text-center md:p-14">
        <p className="display neon text-3xl md:text-4xl">{apply.success}</p>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="apply-form" className="space-y-10">
      <div className="grid gap-5 sm:grid-cols-2">
        <F label="NAME">
          <Input data-testid="apply-name" required value={form.name} onChange={set("name")}
            className="rounded-sm bg-[#0A0A0A]" placeholder="Jordan Ahquin" />
        </F>
        <F label="EMAIL">
          <Input data-testid="apply-email" type="email" required value={form.email}
            onChange={set("email")} className="rounded-sm bg-[#0A0A0A]" placeholder="you@email.com" />
        </F>
        <F label="PHONE">
          <Input data-testid="apply-phone" value={form.phone} onChange={set("phone")}
            className="rounded-sm bg-[#0A0A0A]" placeholder="801-555-0134" />
        </F>
        <div className="grid grid-cols-2 gap-3">
          <F label="CITY">
            <Input data-testid="apply-city" value={form.city} onChange={set("city")}
              className="rounded-sm bg-[#0A0A0A]" placeholder="Provo" />
          </F>
          <F label="STATE">
            <Input data-testid="apply-state" value={form.state} onChange={set("state")}
              className="rounded-sm bg-[#0A0A0A]" placeholder="UT" />
          </F>
        </div>
      </div>

      <div className="space-y-5">
        <F label="WHY ARE YOU INTERESTED IN STARTING A BUSINESS?">
          <Textarea data-testid="apply-why" value={form.why_business} onChange={set("why_business")}
            rows={3} className="rounded-sm bg-[#0A0A0A]" />
        </F>
        <F label="WHAT ARE YOU DOING CURRENTLY?">
          <Textarea data-testid="apply-current" value={form.current_work} onChange={set("current_work")}
            rows={2} className="rounded-sm bg-[#0A0A0A]" />
        </F>
        <F label="WHAT WOULD OWNING A BUSINESS CHANGE FOR YOU?">
          <Textarea data-testid="apply-change" value={form.what_would_change}
            onChange={set("what_would_change")} rows={3} className="rounded-sm bg-[#0A0A0A]" />
        </F>
      </div>

      <div className="space-y-6">
        <F label="WOULD YOU OPERATE THIS YOURSELF?">
          <Pick value={form.operate_yourself} onChange={pick("operate_yourself")}
            options={YES_NO} testId="apply-operate" />
        </F>
        <F label="ARE YOU COMFORTABLE WORKING EVENTS AND INTERACTING WITH CUSTOMERS?">
          <Pick value={form.comfortable_with_events} onChange={pick("comfortable_with_events")}
            options={YES_NO} testId="apply-comfortable" />
        </F>
        <F label="WHICH PATH INTERESTS YOU?">
          <Pick value={form.path} onChange={pick("path")}
            options={apply.path_options || []} testId="apply-path" />
        </F>
        <F label="WHEN WOULD YOU LIKE TO START?">
          <Pick value={form.start_timeline} onChange={pick("start_timeline")}
            options={apply.timeline_options || []} testId="apply-timeline" />
        </F>
      </div>

      {error ? (
        <div data-testid="apply-error"
          className="border border-destructive/60 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <Button data-testid="apply-submit" type="submit" disabled={busy}
        className="display w-full rounded-sm bg-primary py-7 text-2xl uppercase text-primary-foreground hover:bg-[#E6C800]">
        {busy ? "Sending..." : "Apply to become an operator"}
      </Button>
      <p className="mono text-[10px] leading-relaxed tracking-widest text-muted-foreground">
        NO PAYMENT IS TAKEN HERE. THIS IS AN APPLICATION AND QUALIFICATION PROCESS.
      </p>
    </form>
  );
};

export const ApplyIntro = ({ apply }) => (
  <>
    <Eyebrow>THE NEXT STEP</Eyebrow>
    <h2 className="neon mt-3 text-4xl leading-tight sm:text-5xl">{apply.title}</h2>
    <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">{apply.intro}</p>
  </>
);
